import { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { CONTRACTS, ABIS, ILK } from '../utils/contracts';
import {
  WAD, RAY, RAD,
  oraclePlsPriceWad, jdaiTargetWad, matToWad, wdiv,
  healthRatioWad, maxDebtWad, liquidationPriceWad, plsRequiredForDebtFloor,
  debtWadFromArt, wadToNumber, numberToWad
} from '../utils/vaultMath';
import { usePulseXPrice } from './usePulseXPrice';

export const useVault = (signer, account) => {
  const { jdaiMarketPrice, marketPlsPrice, loading: priceLoading } = usePulseXPrice();

  const [vaultData, setVaultData] = useState({
    collateral: '0',
    debt: '0',
    healthRatio: 0,
    collateralization: 0,
    maxDebt: '0',
    liquidationPrice: '0',
    isLoading: true
  });
  const [systemData, setSystemData] = useState({
    plsPrice: '0',
    jdaiPrice: '0',
    liquidationRatio: '1.5',
    stabilityFee: null,
    debtCeiling: '0',
    totalDebt: '0',
    availableDebt: '0',
    minDebt: '10',
    plsRequiredForMinDebt: '0',
    isLoading: true
  });

  const [balances, setBalances] = useState({
    pls: '0',
    jdai: '0',
    isLoading: true
  });

  // Fetch vault data
  const fetchVaultData = async () => {
    if (!signer || !account) {
      setVaultData(prev => ({ ...prev, isLoading: false }));
      return;
    }

    try {
      const vat = new ethers.Contract(CONTRACTS.VAT, ABIS.VAT, signer);
      const spotter = new ethers.Contract(CONTRACTS.SPOTTER, ABIS.SPOTTER, signer);
      const jug = new ethers.Contract(CONTRACTS.JUG, ABIS.JUG, signer);
      // Get vault (urn) data
      const [ink, art] = await vat.urns(ILK, account);
      const [totalDebt, rate, spot, debtCeiling, minDebt] = await vat.ilks(ILK);
        // Get system parameters
      const par = await spotter.par();
      const [, mat] = await spotter.ilks(ILK);

      // Stability fee from the Jug (duty and base are per-second growth factors, RAY).
      let stabilityFee = null;
      try {
        const [duty] = await jug.ilks(ILK);
        const base = await jug.base();
        const perSecond = wadToNumber(((duty + base) * WAD) / RAY);
        stabilityFee = ((Math.pow(perSecond, 31536000) - 1) * 100).toFixed(2);
      } catch (e) {
        console.warn('Could not read stability fee from Jug', e);
      }

      // --- Prices: oracle is the safety basis; market price is informational only ---
      // Oracle PLS price (USD, WAD) derived from Vat spot and Spotter mat/par.
      const oracleWad = oraclePlsPriceWad(spot, mat, par);
      let plsPriceWad = oracleWad;
      if (oracleWad <= 0n && marketPlsPrice > 0) {
        // Spotter not poked / spot unset — fall back to market price for display.
        plsPriceWad = numberToWad(marketPlsPrice);
      }

      const targetWad = jdaiTargetWad(par);              // JDAI gold-pegged target (USD, WAD)
      const jdaiOracleTarget = wadToNumber(targetWad);

      // Vault metrics — true debt is art * rate (the tab the Vat enforces), not raw art.
      // Health & liquidation use the oracle price so the figures match the Vat.
      const debtWad = debtWadFromArt(art, rate);
      const healthWad = healthRatioWad(ink, plsPriceWad, debtWad, targetWad);
      // Normalize health so 1.0 = the enforced collateral minimum (mat), not 100%.
      const matWad = matToWad(mat);
      const healthNormWad = healthWad === null ? null : wdiv(healthWad, matWad);
      const maxDebt = maxDebtWad(ink, plsPriceWad, mat, targetWad);

      let liqPriceWad;
      if (ink > 0n && debtWad > 0n) {
        liqPriceWad = liquidationPriceWad(ink, debtWad, targetWad, mat);
      } else {
        // Empty vault: informational break-even (oracle price / liquidation ratio).
        liqPriceWad = mat > 0n ? (plsPriceWad * RAY) / mat : 0n;
      }

      const oraclePlsPriceNum = wadToNumber(oracleWad);
      const debtCeilingNum = wadToNumber((debtCeiling * WAD) / RAD); // line: RAD -> WAD
      const totalDebtNum = wadToNumber(totalDebt);                   // Art (WAD)

      setVaultData({
        // Full-precision strings — display code formats these; wizard math must not
        // inherit rounded-up values that overshoot on-chain balances.
        collateral: ethers.formatEther(ink),
        debt: ethers.formatEther(debtWad),
        healthRatio: healthNormWad === null ? Infinity : wadToNumber(healthNormWad),
        collateralization: healthWad === null ? Infinity : wadToNumber(healthWad),
        maxDebt: wadToNumber(maxDebt).toString(),
        liquidationPrice: wadToNumber(liqPriceWad).toFixed(8),
        isLoading: false
      });

      setSystemData({
        plsPrice: oraclePlsPriceNum.toString(),        // shown as "PLS Oracle Info"
        jdaiPrice: jdaiMarketPrice > 0 ? jdaiMarketPrice.toString() : jdaiOracleTarget.toString(),
        liquidationRatio: wadToNumber(matWad).toString(),
        stabilityFee,                                  // % APY from the Jug, or null
        debtCeiling: debtCeilingNum.toString(),
        totalDebt: totalDebtNum.toString(),
        availableDebt: Math.max(0, debtCeilingNum - totalDebtNum).toString(),
        minDebt: wadToNumber((minDebt * WAD) / RAD).toString(),        // dust: RAD -> WAD (JDAI floor)
        plsRequiredForMinDebt: wadToNumber(plsRequiredForDebtFloor(minDebt, spot)).toString(),
        marketPlsPrice: marketPlsPrice.toString(),
        marketJdaiPrice: jdaiMarketPrice.toString(),
        oraclePlsPrice: oraclePlsPriceNum.toString(),
        oracleJdaiTarget: jdaiOracleTarget.toString(),
        goldBasedTarget: jdaiOracleTarget.toString(),
        jdaiPremium: jdaiMarketPrice > 0 && jdaiOracleTarget > 0
          ? ((jdaiMarketPrice / jdaiOracleTarget - 1) * 100).toString() : '0',
        effectivePlsPrice: wadToNumber(plsPriceWad).toString(),
        isLoading: false
      });

    } catch (error) {
      console.error('Error fetching vault data:', error);
      setVaultData(prev => ({ ...prev, isLoading: false }));
      setSystemData(prev => ({ ...prev, isLoading: false }));
    }
  };

  // Fetch balances
  const fetchBalances = async () => {
    if (!signer || !account) {
      setBalances(prev => ({ ...prev, isLoading: false }));
      return;
    }

    try {
      const provider = signer.provider;
      const jdai = new ethers.Contract(CONTRACTS.JDAI, ABIS.JDAI, signer);

      // Get PLS balance
      const plsBalance = await provider.getBalance(account);
      
      // Get JDAI balance
      const jdaiBalance = await jdai.balanceOf(account);

      setBalances({
        // Full precision — rounding here previously let "Max" exceed real balances.
        pls: ethers.formatEther(plsBalance),
        jdai: ethers.formatEther(jdaiBalance),
        isLoading: false
      });

    } catch (error) {
      console.error('Error fetching balances:', error);
      setBalances(prev => ({ ...prev, isLoading: false }));
    }
  };

  // Clear debt using existing internal DAI balance (for users who burned JDAI but still have debt)
  const clearDebtWithInternalBalance = async () => {
    if (!signer || !account) throw new Error('Wallet not connected');

    const vat = new ethers.Contract(CONTRACTS.VAT, ABIS.VAT, signer);

    try {
      // Get current debt amount
      const [, art] = await vat.urns(ILK, account);

      if (art === 0n) {
        throw new Error('No debt to clear');
      }
      
      // Get current internal DAI balance (in RAD - 45 decimals)
      const daiBalance = await vat.dai(account);
      
      // Convert art (wad) to tab (rad) for comparison
      const ilkData = await vat.ilks(ILK);
      const rate = ilkData.rate;
      
      const debtInRad = art * rate;
      
      if (daiBalance < debtInRad) {
        throw new Error(`Insufficient internal DAI balance. Need: ${ethers.formatUnits(debtInRad, 45)} DAI, Have: ${ethers.formatUnits(daiBalance, 45)} DAI`);
      }

      
      // Use frob to clear debt (negative dart reduces debt)
      const frobTx = await vat.frob(
        ILK,            // ilk: PLS-A
        account,        // u: urn owner
        account,        // v: collateral source (not used)
        account,        // w: debt destination
        0,              // dink: no collateral change
        -art            // dart: negative to reduce debt (art is already BigInt)
      );
      await frobTx.wait();
      
      return frobTx;
    } catch (error) {
      console.error('Error in clearDebtWithInternalBalance:', error);
      throw error;
    }
  };// Refresh all data
  const refresh = async () => {
    await Promise.all([
      fetchVaultData(),
      fetchBalances()
    ]);
  };

  // Auto-refresh on account/signer change
  useEffect(() => {
    // Only load vault data after PulseX prices have been fetched (or failed to load)
    if (signer && account && !priceLoading) {
      refresh();    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signer, account, priceLoading, jdaiMarketPrice, marketPlsPrice]);
  // Contract instances for the wizard
  const contracts = signer ? {
    vat: new ethers.Contract(CONTRACTS.VAT, ABIS.VAT, signer),
    pls: new ethers.Contract(CONTRACTS.PLS, ABIS.PLS, signer),
    jdai: new ethers.Contract(CONTRACTS.JDAI, ABIS.JDAI, signer),
    ethjoin: new ethers.Contract(CONTRACTS.ETHJOIN, ABIS.ETHJOIN, signer),
    daijoin: new ethers.Contract(CONTRACTS.DAIJOIN, ABIS.DAIJOIN, signer)
  } : null;

  return {
    vaultData,
    systemData,
    balances,
    contracts,
    clearDebtWithInternalBalance,
    refresh
  };
};
